import { TenantSlug } from "../sharedConfig.mjs";
import { oidcSecretName } from "./types.mjs";
import { PAYORS } from "../payors/registry.mjs";
import { eligibilityPayors, priorAuthPayors } from "../payors/tenantPayors.mjs";
import "../payors/index.mjs";
import { demo } from "./demo/index.mjs";
import { hhb } from "./hhb/index.mjs";
import { skylight } from "./skylight/index.mjs";

//#region src/tenants/index.ts
/**
* One declarative identity per live TenantSlug. Adding a slug is a type error
* until a matching file exists under this folder and is wired in here.
*
* Harness prompts stay in packages/services/src/harnesses/. Libretto workflow
* implementations stay in apps/libretto-automations/src/tenants/.
*/
const tenantManifests = {
	[TenantSlug.HHB]: hhb,
	[TenantSlug.Skylight]: skylight,
	[TenantSlug.Demo]: demo
};
function getTenantManifest(tenantSlug) {
	return tenantManifests[tenantSlug];
}
/** Payors this tenant can file a prior authorization for. */
function priorAuthPayorsForTenant(slug) {
	return priorAuthPayors(getTenantManifest(slug).clinic.payors);
}
/**
* Payors this tenant can run an eligibility check for. Wider than the
* prior-auth set, and deliberately separate: the web sidebar used to gate
* eligibility on the prior-auth list and refused checks the API would have run.
*/
function eligibilityPayorsForTenant(slug) {
	return eligibilityPayors(getTenantManifest(slug).clinic.payors);
}
/**
* Supported payors as they appear in the insurance-modal dropdown, for refusal
* messages.
*
* The label rather than the colloquial name, because the message's whole job is
* to send someone to that dropdown. Naming "Humana" when the dropdown offers
* both "Humana" and "Humana Insurance Company", and only the second can be
* filed, refuses them a second time for following the instruction.
*/
function payorLabelsForStaff(payors) {
	return payors.map((p) => PAYORS[p].label).sort();
}
/**
* Demo presenter flags, or undefined for every other slug. Callers branch on
* this object instead of hard-coding `tenantSlug === TenantSlug.Demo`.
*/
function getDemoFeatures(slug) {
	return getTenantManifest(slug).demoFeatures;
}
/**
* Whether this tenant's synced referrals go straight to the agent.
*
* A function rather than a raw manifest read at the call site, so the sync path
* does not have to reach for the whole manifest to answer one boolean.
*/
function autoAssignsReferralsToAgent(slug) {
	return getTenantManifest(slug).autoAssignReferralsToAgent;
}
function getSpruceDepartmentEndpoint(tenantSlug, department) {
	return getTenantManifest(tenantSlug).spruce?.departmentEndpoints[department];
}
/**
* Whether these two slugs sit on the same Spruce org (Skylight and Demo do).
*
* False when either has no org, so two non-Spruce tenants never match on a
* pair of undefineds.
*/
function tenantsShareSpruceOrg(a, b) {
	const orgA = getTenantManifest(a).spruce?.orgId;
	const orgB = getTenantManifest(b).spruce?.orgId;
	return orgA !== void 0 && orgA === orgB;
}
function pickFromManifests(key) {
	const result = {};
	for (const slug of Object.values(TenantSlug)) result[slug] = tenantManifests[slug][key];
	return result;
}
const tenantVendorRegistry = pickFromManifests("vendors");
const tenantLibrettoRegistry = pickFromManifests("libretto");
const TENANT_DISPLAY_NAMES = pickFromManifests("displayName");
const TENANT_SPRUCE_ORG_IDS = Object.fromEntries(Object.values(tenantManifests).flatMap((manifest) => manifest.spruce?.orgId ? [[manifest.slug, manifest.spruce.orgId]] : []));

//#endregion
export { TENANT_DISPLAY_NAMES, TENANT_SPRUCE_ORG_IDS, autoAssignsReferralsToAgent, eligibilityPayorsForTenant, getDemoFeatures, getSpruceDepartmentEndpoint, getTenantManifest, oidcSecretName, payorLabelsForStaff, priorAuthPayorsForTenant, tenantLibrettoRegistry, tenantManifests, tenantVendorRegistry, tenantsShareSpruceOrg };